// src/context/PatientContext.js
// Mirrors AuthContext pattern but for patient sessions.
// Reads previu_patient + previu_token from localStorage (set by PatientLoginPage).

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientAPI } from '../services/api';
import { careTeamAPI } from '../services/careTeamAPI';
import { isLanguage } from '../services/languageAPI';
import { dropPushSubscription } from '../utils/pushSubscription';

const PatientContext = createContext(null);

// Derives the plan the shell displays. Kept here rather than in a component so
// the sidebar chip, the notification bell and the subscription page can never
// disagree about which plan a patient is on.
//
// 12 Aug 2026 — the plan NAME is no longer derived here. The backend resolves
// it once in PatientPlanCatalog.java (Guardian Dubai wins over any domestic
// tier; a null or lapsed subscription reads as "Record Vault Plan") and ships
// it as profile.planLabel / planCode / guardianDubai. This function now only
// decides what the shell does with that answer — whether to nudge an upgrade,
// and what secondary line to show beside it — so the navigator portal and the
// patient portal cannot end up calling the same patient two different things.
//
// Three states, in precedence order:
//   1. Covered by a subscriber — an NRI subscriber enrolled this patient, so
//      their care is paid for under the subscriber's plan and this patient must
//      not be sold one of their own. PatientPlanController is explicitly for a
//      patient paying for themselves; the two are separate systems by design
//      (see its Javadoc), and nothing reconciles a double purchase.
//   2. On a paid tier — subscriptionStatus ACTIVE and not past renewal.
//   3. Record Vault — everyone else. There is no "no plan" state in the
//      product: a patient without a subscription is on the baseline tier.
export function derivePlan(profile) {
  if (!profile) {
    return { loading: true, isPaid: false, coveredBySubscriber: false, label: '—' };
  }

  // planLabel is absent only on a payload that predates it; fall back to the
  // baseline name rather than rendering a blank chip.
  const label = profile.planLabel || 'Record Vault Plan';
  const guardianDubai = Boolean(profile.guardianDubai);

  if (profile.subscriberId || guardianDubai) {
    return {
      loading: false,
      // Covered counts as paid for prompting purposes — never nudge this
      // patient to buy a plan they already benefit from.
      isPaid: true,
      coveredBySubscriber: true,
      guardianDubai,
      subscriberName: profile.subscriberName || null,
      label,
      detail: profile.subscriberName ? `by ${profile.subscriberName}` : 'by a subscriber',
    };
  }

  const renewalAt = profile.subscriptionRenewalAt ? new Date(profile.subscriptionRenewalAt) : null;
  const active = profile.subscriptionStatus === 'ACTIVE' && (!renewalAt || renewalAt > new Date());

  if (active) {
    return {
      loading: false,
      isPaid: true,
      coveredBySubscriber: false,
      guardianDubai: false,
      label,
      detail: renewalAt
        ? `renews ${renewalAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
        : 'active',
      renewalAt,
    };
  }

  return {
    loading: false,
    isPaid: false,
    coveredBySubscriber: false,
    guardianDubai: false,
    label,
    detail: 'upgrade for more',
  };
}

export function PatientProvider({ children }) {
  const navigate = useNavigate();
  const [patient, setPatient] = useState(() => {
    try {
      const raw = localStorage.getItem('previu_patient');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  // The cached `patient` above comes from the login response, which is a thin
  // map (token/patientId/name/phone/email) carrying no subscription or
  // subscriber fields at all — and it would go stale the moment a patient
  // subscribes or a navigator links them to a subscriber. `profile` is the
  // live record from GET /api/patients/{id}, which a patient may read for
  // themselves (PatientAccessGuard.isSelfOrStaff).
  const [profile, setProfile] = useState(null);

  // The patient's care team — fetched in the shell rather than per page, so the
  // navigator can be on screen on every route. null while it has not loaded (or
  // could not load), which the shell renders differently from a loaded team
  // that has no navigator in it: the first is "we don't know yet", the second
  // is a patient with nobody assigned, and telling a patient the wrong one of
  // those is worse than showing nothing.
  const [careTeam, setCareTeam] = useState(null);

  // Keep in sync if another tab logs in/out
  useEffect(() => {
    function onStorage(e) {
      if (e.key === 'previu_patient') {
        try {
          setPatient(e.newValue ? JSON.parse(e.newValue) : null);
        } catch { setPatient(null); }
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const patientId = patient?.id;

  const refreshProfile = useCallback(() => {
    if (!patientId) { setProfile(null); return; }
    patientAPI.get(patientId)
      .then(res => setProfile(res.data))
      // Silent by design: the shell falls back to the cached patient for name
      // and initials, and the plan chip simply doesn't render. A failed
      // profile fetch must not block the portal.
      .catch(() => { /* keep whatever we already had */ });
  }, [patientId]);

  useEffect(() => { refreshProfile(); }, [refreshProfile]);

  // Preferred language ('en' | 'ml' | 'hi'), or null while unknown — a profile
  // that hasn't loaded, or a backend that predates the field. The profile's
  // value wins; Settings fills the gap from GET /api/me/language and sets the
  // patient's new choice straight away after a save, until the refreshed
  // profile carries it.
  const [languageOverride, setPreferredLanguage] = useState(null);
  useEffect(() => { setPreferredLanguage(null); }, [profile?.preferredLanguage]);
  const preferredLanguage = languageOverride
    ?? (isLanguage(profile?.preferredLanguage) ? profile.preferredLanguage : null);

  const refreshCareTeam = useCallback(() => {
    if (!patientId) { setCareTeam(null); return; }
    careTeamAPI.list(patientId)
      .then(members => setCareTeam(Array.isArray(members) ? members : []))
      // Silent, for the same reason refreshProfile is: the shell falls back to
      // not showing the navigator, and a failed fetch must not block the portal.
      .catch(() => { /* keep whatever we already had */ });
  }, [patientId]);

  useEffect(() => { refreshCareTeam(); }, [refreshCareTeam]);

  // Profile photo — fetched as a blob and turned into an object URL, rather
  // than any <img src> pointing straight at the API (the endpoint is
  // authenticated, and img tags can't send an Authorization header). Lives
  // here rather than being re-fetched by every consumer (topbar, settings
  // page) — one fetch per patient session, shared. Re-fetches whenever
  // profile.hasAvatar flips (upload/remove), and always revokes the
  // previous object URL so a change doesn't leak one per upload.
  const [avatarUrl, setAvatarUrl] = useState(null);

  useEffect(() => {
    if (!profile?.hasAvatar || !patientId) {
      setAvatarUrl(null);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    patientAPI.getAvatar(patientId)
      .then(res => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setAvatarUrl(objectUrl);
      })
      // Silent, same reasoning as refreshProfile/refreshCareTeam: the
      // topbar/settings page fall back to initials, and a failed avatar
      // fetch must not block the portal.
      .catch(() => { if (!cancelled) setAvatarUrl(null); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [profile?.hasAvatar, patientId]);

  function logout() {
    // Stop push notifications reaching this device for this patient. Reads the
    // token synchronously, so it is safe to clear it on the next line.
    dropPushSubscription({ notifyServer: true });
    localStorage.removeItem('previu_token');
    localStorage.removeItem('previu_patient');
    setPatient(null);
    setProfile(null);
    setCareTeam(null);
    setAvatarUrl(null);
    setPreferredLanguage(null);
    navigate('/login');
  }

  // Sprint 13 fix: same-tab localStorage writes don't fire the 'storage' event,
  // so PatientLoginPage.jsx must call refreshPatient() after writing previu_patient.
  // This re-reads the key and updates React state so child components (e.g.
  // PatientOnboardingPage) get a non-null patient.id immediately after login.
  function refreshPatient() {
    try {
      const raw = localStorage.getItem('previu_patient');
      setPatient(raw ? JSON.parse(raw) : null);
    } catch { setPatient(null); }
  }

  const plan = derivePlan(profile);

  // The assigned navigator, or null. Deliberately keyed on the role the SERVER
  // resolved, not on position in the list: PatientCareTeamController labels an
  // assignment that actually points at a doctor's paired row as DOCTOR (DEF-031),
  // and this must not undo that by treating "the navigator-shaped slot" as a
  // navigator. Named careNavigator rather than navigator so nothing shadows
  // window.navigator where it is destructured.
  const careNavigator = Array.isArray(careTeam)
    ? (careTeam.find(m => m.role === 'NAVIGATOR') ?? null)
    : null;

  return (
    <PatientContext.Provider
      value={{ patient, profile, plan, careTeam, careNavigator, avatarUrl,
               preferredLanguage, setPreferredLanguage,
               logout, refreshPatient, refreshProfile, refreshCareTeam }}
    >
      {children}
    </PatientContext.Provider>
  );
}

export function usePatient() {
  return useContext(PatientContext);
}
